import React from 'react';
import IconExcel from '@assets/images/button/icon_button_excel.svg';
import { media } from '@styles/mediaQuery';
import styled, { css } from 'styled-components';

interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  buttonType: 'default' | 'action' | 'warning' | 'excel' | 'dashed';
  striped?: boolean;
  color?: string;
  disabled?: boolean;
  icon?: string;
  style?: React.CSSProperties;
}

const BaseButton = styled.button`
  padding: 0.5rem 1rem;
  /* min-width: 6rem; */
  border-radius: 0.375rem;
`;

export const Button = ({
  buttonType,
  striped,
  color,
  icon,
  disabled,
  style,
  children,
  ...props
}: ButtonProps) => {
  return (
    <StyledButton
      buttonType={buttonType}
      striped={striped}
      color={color}
      style={style}
      icon={icon}
      disabled={disabled}
      {...props}>
      {icon && <img src={icon} alt="button-icon" />}
      <span>{children}</span>
    </StyledButton>
  );
};

export const StyledButton = styled(BaseButton)<ButtonProps>`
  border: none;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;

  background: ${({ theme, buttonType, color }) =>
    color
      ? color
      : buttonType === 'default'
      ? theme.secondary[600]
      : buttonType === 'action'
      ? theme.primary
      : buttonType === 'excel'
      ? theme.excel
      : buttonType === 'dashed'
      ? 'white'
      : theme.red};

  ${({ buttonType, theme }) =>
    buttonType === 'dashed' &&
    css`
      border: 2px dashed ${theme.neutralGray[300]};
      color: ${theme.neutralGray[400]};
      background: white;
    `}

  ${({ striped, buttonType }) =>
    striped &&
    css`
      background: white;
      border: 1px solid
        ${({ theme }) =>
          buttonType === 'default'
            ? theme.secondary[600]
            : buttonType === 'action'
            ? theme.primary
            : theme.red};
      color: ${({ theme }) =>
        buttonType === 'default'
          ? theme.secondary[600]
          : buttonType === 'action'
          ? theme.primary
          : theme.red};
    `}

  &:hover {
    filter: saturate(150%);
  }
  &:disabled {
    filter: grayscale(100%);
  }

  ${media.minTablet`
    min-width: 6rem;
  `}

  ${media.tablet`
    padding: 0.5rem 0.625rem;
  `}

  @media (max-width: 768px) {
    ${({ icon }) =>
      icon &&
      css`
        span {
          display: none;
        }
      `}
  }
`;

export const ActionButton = styled(StyledButton)`
  ${media.tablet`
    min-width: 6rem;
  `}
`;

export const ToggleButton = styled(BaseButton)<{ enabled?: boolean }>`
  border: 1px solid ${({ theme }) => theme.neutralGray[200]};

  color: ${({ theme, enabled }) => (enabled ? theme.main[600] : 'black')};
`;

export const ExcelButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button buttonType="excel" icon={IconExcel} onClick={onClick}>
      엑셀 출력하기
    </Button>
  );
};

/**
 * IconButton - 아이콘만 있는 버튼
 */
interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'primary' | 'ghost';
}

export const IconButton = ({ icon, size = 'medium', variant = 'default', ...props }: IconButtonProps) => {
  return (
    <StyledIconButton $size={size} $variant={variant} {...props}>
      <img src={icon} alt="icon" />
    </StyledIconButton>
  );
};

const StyledIconButton = styled.button<{ $size: 'small' | 'medium' | 'large'; $variant: 'default' | 'primary' | 'ghost' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return css`
          width: 2rem;
          height: 2rem;
          img {
            width: 1rem;
            height: 1rem;
          }
        `;
      case 'large':
        return css`
          width: 3rem;
          height: 3rem;
          img {
            width: 1.5rem;
            height: 1.5rem;
          }
        `;
      default:
        return css`
          width: 2.5rem;
          height: 2.5rem;
          img {
            width: 1.25rem;
            height: 1.25rem;
          }
        `;
    }
  }}

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background: ${theme.primary};
          &:hover {
            background: ${theme.main[500]};
          }
          img {
            filter: brightness(0) invert(1);
          }
        `;
      case 'ghost':
        return css`
          background: transparent;
          &:hover {
            background: ${theme.neutralGray[100]};
          }
        `;
      default:
        return css`
          background: ${theme.neutralGray[100]};
          &:hover {
            background: ${theme.neutralGray[200]};
          }
        `;
    }
  }}

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:active {
      transform: none;
    }
  }
`;
